import React, { useEffect, useState } from 'react'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts'
import { LineChart } from '@mantine/charts'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store'
import { apiRequest } from '../../utils/api'
import styles from './AdminDashboardBox.module.scss'

const LEVEL_COLORS = [
  '#425AA8',
  '#5B8FF9',
  '#5AD8A6',
  '#F6BD16',
  '#E8684A',
  '#6DC8EC',
  '#9270CA',
  '#FF9D4D',
  '#269A99',
]

const ICONS = {
  centers: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3zm0 2.69L19 12v7h-4v-5H9v5H5v-7l7-6.31z"/>
    </svg>
  ),
  students: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  ),
  lectures: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM8 15c0-1.66 1.34-3 3-3 .35 0 .69.07 1 .18V6h5v2h-3v7.03A3.003 3.003 0 0 1 11 18c-1.66 0-3-1.34-3-3z"/>
    </svg>
  ),
  questions: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.07 12.85c.77-1.39 2.25-2.21 3.11-3.44.91-1.29.4-3.7-2.18-3.7-1.69 0-2.52 1.28-2.87 2.34L7.1 7.05C7.83 4.65 9.76 3 12.01 3c3.79 0 5.58 2.91 4.9 5.92-.37 1.64-1.79 2.58-2.87 3.73-.73.79-.97 1.63-.97 2.5h-2.22c0-1.34.39-2.35 1.22-3.3zM13 18h-2v-2h2v2z"/>
    </svg>
  ),
  newCenter: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
    </svg>
  ),
  newStudent: (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  ),
}

const KPI_CARDS = (data) => [
  {
    icon: ICONS.centers,
    colorClass: 'blue',
    label: 'Total Centers',
    value: data.totalCenters,
    sub: 'Registered centers',
  },
  {
    icon: ICONS.students,
    colorClass: 'green',
    label: 'Total Students',
    value: data.totalStudents,
    sub: 'Active & inactive',
  },
  {
    icon: ICONS.lectures,
    colorClass: 'purple',
    label: 'Total Lectures',
    value: data.totalLectures,
    sub: 'Video content',
  },
  {
    icon: ICONS.questions,
    colorClass: 'teal',
    label: 'Total Questions',
    value: data.totalQuestions,
    sub: 'Question bank',
  },
  {
    icon: ICONS.newCenter,
    colorClass: 'orange',
    label: 'New Centers Today',
    value: data.newCenters,
    sub: 'Registered today',
  },
  {
    icon: ICONS.newStudent,
    colorClass: 'red',
    label: 'New Students Today',
    value: data.newStudents,
    sub: 'Joined today',
  },
]

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className={styles.tooltip}>
      <p className={styles.tooltipLabel}>{label}</p>
      <p className={styles.tooltipValue}>{payload[0].value} students</p>
    </div>
  )
}

const FILTER_OPTIONS = ['전체', '완료만', '미완료만']

const AdminDashboardBox = () => {
  const navigate = useNavigate()

  const [summaryData, setSummaryData] = useState({
    totalCenters: 0,
    totalStudents: 0,
    totalLectures: 0,
    totalQuestions: 0,
    newCenters: 0,
    newStudents: 0,
  })
  const [levelData, setLevelData] = useState([])
  const [averageData, setAverageData] = useState([])
  const [recentRegistrations, setRecentRegistrations] = useState([])
  const [recentboards, setRecentboards] = useState([])

  const [contentSummary, setContentSummary] = useState([])
  const [modalLevel, setModalLevel] = useState(null)
  const [modalData, setModalData] = useState([])
  const [modalFilter, setModalFilter] = useState('전체')
  const [modalLoading, setModalLoading] = useState(false)

  useEffect(() => {
    useAuthStore.setState((prev) => ({
      user: { ...prev.user, CENTER_ID: null },
    }))
    fetchDashBoard()
    fetchLevelStudent()
    fetchMonthlyStats()
    fetchRecentlyCenter()
    fetchContentSummary()
  }, [])

  const fetchDashBoard = async () => {
    try {
      const res = await apiRequest('/fetchDashBoard', 'POST')
      if (res.data) setSummaryData(res.data)
    } catch {}
  }

  const fetchLevelStudent = async () => {
    try {
      const res = await apiRequest('/fetchLevelStudent', 'POST')
      if (res.data) setLevelData(res.data)
    } catch {}
  }

  const fetchMonthlyStats = async () => {
    try {
      const res = await apiRequest('/fetchMonthlyStats', 'POST')
      if (res.data) setAverageData(res.data)
    } catch {}
  }

  const fetchRecentlyCenter = async () => {
    try {
      const res = await apiRequest('/fetchRecentlyCenter', 'POST')
      if (res.data) {
        setRecentRegistrations(res.data.recentRegistrations)
        setRecentboards(res.data.recentboards)
      }
    } catch {}
  }

  const fetchContentSummary = async () => {
    try {
      const res = await apiRequest('/api/content-status', 'GET')
      if (res.success) {
        const ALL_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
        const dataMap = {}
        res.data.forEach((d) => { dataMap[Number(d.LEVEL)] = d })
        const filled = ALL_LEVELS.map((lv) =>
          dataMap[lv] || { LEVEL: lv, total: 0, completed: 0, incomplete: 0 }
        )
        setContentSummary(filled)
      }
    } catch {}
  }

  const openLevelModal = async (level) => {
    setModalLevel(level)
    setModalFilter('전체')
    setModalLoading(true)
    setModalData([])
    try {
      const res = await apiRequest('/api/content-status', 'GET', { level })
      if (res.success) setModalData(res.data)
    } catch {}
    setModalLoading(false)
  }

  const closeModal = () => setModalLevel(null)

  const isComplete = (row) => Number(row.questionCount) > 0 && Number(row.hasVideo) === 1

  const filteredModalData = modalData.filter((row) => {
    if (modalFilter === '완료만') return isComplete(row)
    if (modalFilter === '미완료만') return !isComplete(row)
    return true
  })

  const getInitial = (name) => (name ? name[0].toUpperCase() : '?')

  const handleBarClick = (data) => {
    const selectedLevel = data?.activePayload?.[0]?.payload?.level?.replace('Level ', '')
    if (selectedLevel) navigate('/studentInfo', { state: { level: selectedLevel } })
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2>Admin Overview</h2>
        <p>Platform-wide stats and activity at a glance.</p>
      </div>

      {/* KPI Cards — 6 cards in 3-column grid */}
      <div className={styles.kpiRow}>
        {KPI_CARDS(summaryData).map((card) => (
          <div key={card.label} className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles[card.colorClass]}`}>
              {card.icon}
            </div>
            <div className={styles.kpiBody}>
              <span className={styles.kpiLabel}>{card.label}</span>
              <span className={styles.kpiValue}>{card.value ?? 0}</span>
              <span className={styles.kpiSub}>{card.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className={styles.mainGrid}>
        {/* Left: Charts */}
        <div className={styles.chartsCol}>
          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Student Distribution by Level</h3>
              <span className={styles.chartNote}>Click a bar to view students</span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <ReBarChart
                data={levelData}
                margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                onClick={handleBarClick}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="level"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="students" radius={[6, 6, 0, 0]} maxBarSize={48}>
                  {levelData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={LEVEL_COLORS[index % LEVEL_COLORS.length]}
                    />
                  ))}
                </Bar>
              </ReBarChart>
            </ResponsiveContainer>

            {levelData.length > 0 && (
              <div className={styles.legend}>
                {levelData.map((item, index) => (
                  <div key={item.level} className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: LEVEL_COLORS[index % LEVEL_COLORS.length] }}
                    />
                    <span className={styles.legendLabel}>{item.level}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Monthly Registration Trends</h3>
            </div>
            <LineChart
              h={220}
              data={averageData}
              dataKey="month"
              series={[
                { name: 'student', color: '#425AA8', label: 'Students' },
                { name: 'center', color: '#5AD8A6', label: 'Centers' },
              ]}
              curveType="monotone"
              gridAxis="xy"
            />
          </div>
        </div>

        {/* Right: Side panels */}
        <div className={styles.sideCol}>
          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Recently Registered Centers</h3>
              <button
                className={styles.viewAllBtn}
                onClick={() => navigate('/centers')}
              >
                View All
              </button>
            </div>
            {recentRegistrations.length === 0 ? (
              <p className={styles.listEmpty}>No recent registrations.</p>
            ) : (
              recentRegistrations.map((item, i) => (
                <div
                  key={i}
                  className={styles.listItem}
                  onClick={() => navigate('/centers')}
                >
                  <div className={styles.listLeft}>
                    <div className={styles.listAvatar}>{getInitial(item.NAME)}</div>
                    <span className={styles.listName}>{item.NAME}</span>
                  </div>
                  <span className={styles.listDate}>{item.REG_DTM}</span>
                </div>
              ))
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.chartHeader}>
              <h3 className={styles.cardTitle}>Recent Inquiries</h3>
              <button
                className={styles.viewAllBtn}
                onClick={() => navigate('/Inquiries')}
              >
                View All
              </button>
            </div>
            {recentboards.length === 0 ? (
              <p className={styles.listEmpty}>No recent inquiries.</p>
            ) : (
              recentboards.map((item, i) => (
                <div
                  key={i}
                  className={styles.listItem}
                  onClick={() => navigate('/Inquiries')}
                >
                  <div className={styles.listLeft}>
                    <div className={styles.listAvatar}>Q</div>
                    <span className={styles.listTitle}>{item.TITLE}</span>
                  </div>
                  <span className={styles.listDate}>{item.REG_DTM}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 콘텐츠 현황 */}
      {contentSummary.length > 0 && (
        <div className={styles.contentSection}>
          <h3 className={styles.sectionTitle}>콘텐츠 현황</h3>
          <div className={styles.contentGrid}>
            {contentSummary.map((item) => {
              const total = Number(item.total)
              const completed = Number(item.completed)
              const incomplete = Number(item.incomplete)
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0
              const status = pct === 100 ? 'done' : pct === 0 ? 'empty' : 'progress'
              return (
                <div
                  key={item.LEVEL}
                  className={`${styles.contentCard} ${styles[`card_${status}`]}`}
                  onClick={() => openLevelModal(item.LEVEL)}
                >
                  <div className={styles.contentCardTop}>
                    <span className={styles.contentLevel}>Level {item.LEVEL}</span>
                    <span className={styles.contentPct}>{pct}%</span>
                  </div>
                  <div className={styles.contentBar}>
                    <div className={`${styles.contentBarFill} ${styles[`bar_${status}`]}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className={styles.contentCardBottom}>
                    <span className={styles.contentTotal}>{total}챕터</span>
                    <span className={styles.contentCompleted}>완료 {completed}</span>
                    {incomplete > 0 && (
                      <span className={styles.contentIncomplete}>미완료 {incomplete}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {modalLevel !== null && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Level {modalLevel} — 챕터 목록</h3>
              <button className={styles.modalClose} onClick={closeModal}>✕</button>
            </div>

            <div className={styles.modalFilters}>
              {FILTER_OPTIONS.map((f) => (
                <button
                  key={f}
                  className={`${styles.filterBtn} ${modalFilter === f ? styles.filterBtnActive : ''}`}
                  onClick={() => setModalFilter(f)}
                >
                  {f}
                </button>
              ))}
              <span className={styles.filterCount}>{filteredModalData.length}개</span>
            </div>

            {modalLoading ? (
              <p className={styles.modalEmpty}>불러오는 중...</p>
            ) : filteredModalData.length === 0 ? (
              <p className={styles.modalEmpty}>챕터가 없습니다.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.chapterTable}>
                  <thead>
                    <tr>
                      <th>라벨</th>
                      <th>챕터명</th>
                      <th>문제 수</th>
                      <th>영상</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModalData.map((row) => {
                      const qCount = Number(row.questionCount)
                      const done = qCount > 0
                      return (
                        <tr key={row.LEVEL_ID}>
                          <td className={styles.tdLabel}>{row.label}</td>
                          <td className={styles.tdName}>{row.chapterName}</td>
                          <td className={styles.tdQ}>
                            {done
                              ? <span className={styles.badge + ' ' + styles.badgeOk}>{qCount}개</span>
                              : <span className={styles.badge + ' ' + styles.badgeNo}>없음</span>
                            }
                          </td>
                          <td className={styles.tdV}>
                            {Number(row.hasVideo) === 1
                              ? <span className={styles.badge + ' ' + styles.badgeOk}>있음</span>
                              : <span className={styles.badge + ' ' + styles.badgeNo}>없음</span>
                            }
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboardBox
